import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import axios from "../../util/axiosInstance";
import { toast } from "react-toastify";
import { sortAlphabetically } from "../../../utils/sortAlphabetically";
import { getUIDescription } from "../../../components/SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

type Props = {
  open: boolean;
  onClose: () => void;
  onAdded?: () => void;
};

type Group = {
  id: string;
  name: string;
};

export default function AddReportGroup({ open, onClose, onAdded }: Props) {
  const [reportGroupName, setReportGroupName] = useState("");
  const [reportGroupDesc, setReportGroupDesc] = useState("");
  const [customerGroupId, setCustomerGroupId] = useState("");
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/groups?type=customer`,
          {
            params: { companyId: process.env.REACT_APP_COMPANY_ID },
          }
        );

        const sortedData = sortAlphabetically(response.data, "name");
        setGroups(sortedData);
      } catch (err) {
        console.error("❌ Error fetching groups:", err);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchGroups();
  }, []);

  const handleSubmit = async () => {
    if (!reportGroupName || !customerGroupId) {
      toast.warning("Please fill all required fields");
      return;
    }

    try {
      const payload = {
        name: reportGroupName,
        description: reportGroupDesc,
        groupId: customerGroupId,
        companyId: process.env.REACT_APP_COMPANY_ID,
        UI_Description: `${getUIDescription()}\n Add Report Group`, 
      };

      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reportGroups`,
        payload
      );

      toast.success("Report group created successfully!");
      setReportGroupName("");
      setReportGroupDesc("");
      setCustomerGroupId("");

      onClose();
      if (onAdded) onAdded();
    } catch (err) {
      console.error("❌ Error creating report group:", err);
      toast.error("Failed to create report group");
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* BACKDROP */}
        <div className="fixed inset-0 bg-black bg-opacity-50" />

        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
          <Dialog.Panel className="w-full max-w-lg rounded-lg bg-slate-800 p-6 shadow-xl text-white">
            {/* HEADER */}
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-xl font-semibold">
                Add Report Group
              </Dialog.Title>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-xl"
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="space-y-5">
              {/* Row 1 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm">Report Group Name *</label>
                  <input
                    type="text"
                    value={reportGroupName}
                    onChange={(e) => setReportGroupName(e.target.value)}
                    className="mt-1 w-full rounded bg-slate-700 px-3 py-2 border border-gray-600 text-white"
                  />
                </div>

                <div>
                  <label className="text-sm">Report Group Description</label>
                  <input
                    type="text"
                    value={reportGroupDesc}
                    onChange={(e) => setReportGroupDesc(e.target.value)}
                    className="mt-1 w-full rounded bg-slate-700 px-3 py-2 border border-gray-600 text-white"
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div>
                <label className="text-sm">Customer Group *</label>
                <select
                  value={customerGroupId}
                  onChange={(e) => setCustomerGroupId(e.target.value)}
                  disabled={loadingGroups}
                  className="mt-1 w-full rounded bg-slate-700 px-3 py-2 border border-gray-600 text-white"
                >
                  {groups.length > 0 ? (
                    groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>
                      {loadingGroups
                        ? "Loading groups..."
                        : "No groups available"}
                    </option>
                  )}
                </select>
              </div>
            </div>

            {/* FOOTER BUTTONS */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded bg-gray-600 hover:bg-gray-700"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
